/**
 * Reusable form for defining a MarketFit product/solution (inputs and benefits).
 * Used by ProductDefinitionDrawer and MarketFitSolutionPage.
 */
import React from 'react';
import { Button, Input, Select, Table, Spin, Typography, Segmented } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  type ProductInputsBenefits,
  type ProductInputItem,
  type ProductBenefitItem,
  type ProductSchemaV2,
  type FunctionalIntervention,
  MARKETFIT_CATEGORIES,
  INTERVENTION_EFFECTS,
  generateId,
} from '../../types/marketFitLocalBridge';

const categoryOptions = MARKETFIT_CATEGORIES.map((c) => ({ value: c, label: c }));

export interface ProductDefinitionFormProps {
  data: ProductInputsBenefits;
  dataV2: ProductSchemaV2;
  schemaMode: 'legacy' | 'v2';
  onDataChange: (data: ProductInputsBenefits) => void;
  onDataV2Change: (data: ProductSchemaV2) => void;
  onSchemaModeChange: (mode: 'legacy' | 'v2') => void;
  onSave: () => void;
  loading?: boolean;
  saving?: boolean;
  productId: string | null;
  showFooter?: boolean;
}

export const ProductDefinitionForm: React.FC<ProductDefinitionFormProps> = ({
  data,
  dataV2,
  schemaMode,
  onDataChange,
  onDataV2Change,
  onSchemaModeChange,
  onSave,
  loading = false,
  saving = false,
  productId,
  showFooter = true,
}) => {
  const addInput = () =>
    onDataChange({
      ...data,
      inputs: [
        ...data.inputs,
        { id: generateId(), name: '', category: 'cost', isRequired: true },
      ],
    });
  const removeInput = (id: string) =>
    onDataChange({ ...data, inputs: data.inputs.filter((i) => i.id !== id) });
  const updateInput = (id: string, field: keyof ProductInputItem, value: string | boolean) =>
    onDataChange({
      ...data,
      inputs: data.inputs.map((i) => (i.id === id ? { ...i, [field]: value } : i)),
    });

  const addBenefit = () =>
    onDataChange({
      ...data,
      benefits: [...data.benefits, { id: generateId(), name: '', category: 'cost' }],
    });
  const removeBenefit = (id: string) =>
    onDataChange({ ...data, benefits: data.benefits.filter((b) => b.id !== id) });
  const updateBenefit = (id: string, field: keyof ProductBenefitItem, value: string) =>
    onDataChange({
      ...data,
      benefits: data.benefits.map((b) => (b.id === id ? { ...b, [field]: value } : b)),
    });

  const addFunctionalIntervention = () =>
    onDataV2Change({
      ...dataV2,
      functional_interventions: [
        ...dataV2.functional_interventions,
        { target_constraint: '', effect: 'reduce', magnitude: 0 },
      ],
    });
  const removeFunctionalIntervention = (index: number) =>
    onDataV2Change({
      ...dataV2,
      functional_interventions: dataV2.functional_interventions.filter((_, i) => i !== index),
    });
  const setFunctionalIntervention = (index: number, field: keyof FunctionalIntervention, value: string | number) =>
    onDataV2Change({
      ...dataV2,
      functional_interventions: dataV2.functional_interventions.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      ),
    });

  const content = (
    <Spin spinning={loading}>
      {!productId ? (
        <Typography.Text type="secondary">Select a product to define.</Typography.Text>
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <Segmented
              options={[
                { value: 'legacy', label: 'Legacy (inputs & benefits)' },
                { value: 'v2', label: 'Core schema v2 (interventions)' },
              ]}
              value={schemaMode}
              onChange={(v) => onSchemaModeChange(v as 'legacy' | 'v2')}
            />
          </div>
          {schemaMode === 'v2' ? (
            <>
              <Typography.Title level={5}>Functional interventions (core schema v2)</Typography.Title>
              <Table
                size="small"
                pagination={false}
                dataSource={dataV2.functional_interventions.map((row, i) => ({ ...row, _index: i }))}
                rowKey="_index"
                columns={[
                  {
                    title: 'Target constraint',
                    width: 140,
                    render: (_, r) => (
                      <Input
                        value={r.target_constraint}
                        onChange={(e) => setFunctionalIntervention(r._index, 'target_constraint', e.target.value)}
                        placeholder="e.g. capacity"
                      />
                    ),
                  },
                  {
                    title: 'Effect',
                    width: 120,
                    render: (_, r) => (
                      <Select
                        value={r.effect}
                        onChange={(v) => setFunctionalIntervention(r._index, 'effect', v)}
                        options={INTERVENTION_EFFECTS.map((e) => ({ value: e, label: e }))}
                        style={{ width: '100%' }}
                      />
                    ),
                  },
                  {
                    title: 'Magnitude',
                    width: 90,
                    render: (_, r) => (
                      <Input
                        type="number"
                        value={r.magnitude}
                        onChange={(e) =>
                          setFunctionalIntervention(r._index, 'magnitude', Number(e.target.value) || 0)
                        }
                      />
                    ),
                  },
                  {
                    width: 50,
                    render: (_, r) => (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeFunctionalIntervention(r._index)}
                      />
                    ),
                  },
                ]}
              />
              <Button type="dashed" icon={<PlusOutlined />} onClick={addFunctionalIntervention} style={{ marginTop: 8 }}>
                Add intervention
              </Button>
            </>
          ) : (
            <>
              <Typography.Title level={5}>Inputs (what the product needs)</Typography.Title>
              <Table
                size="small"
                pagination={false}
                dataSource={data.inputs}
                rowKey="id"
                columns={[
                  {
                    title: 'Name',
                    dataIndex: 'name',
                    render: (_, r) => (
                      <Input
                        placeholder="Input name"
                        value={r.name}
                        onChange={(e) => updateInput(r.id, 'name', e.target.value)}
                      />
                    ),
                  },
                  {
                    title: 'Category',
                    width: 140,
                    render: (_, r) => (
                      <Select
                        value={r.category}
                        onChange={(v) => updateInput(r.id, 'category', v)}
                        options={categoryOptions}
                        style={{ width: '100%' }}
                      />
                    ),
                  },
                  {
                    width: 50,
                    render: (_, r) => (
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeInput(r.id)} />
                    ),
                  },
                ]}
              />
              <Button type="dashed" icon={<PlusOutlined />} onClick={addInput} style={{ marginTop: 8 }}>
                Add input
              </Button>

              <Typography.Title level={5} style={{ marginTop: 24 }}>
                Benefits (product outputs)
              </Typography.Title>
              <Table
                size="small"
                pagination={false}
                dataSource={data.benefits}
                rowKey="id"
                columns={[
                  {
                    title: 'Name',
                    dataIndex: 'name',
                    render: (_, r) => (
                      <Input
                        placeholder="Benefit name"
                        value={r.name}
                        onChange={(e) => updateBenefit(r.id, 'name', e.target.value)}
                      />
                    ),
                  },
                  {
                    title: 'Category',
                    width: 140,
                    render: (_, r) => (
                      <Select
                        value={r.category}
                        onChange={(v) => updateBenefit(r.id, 'category', v)}
                        options={categoryOptions}
                        style={{ width: '100%' }}
                      />
                    ),
                  },
                  {
                    width: 50,
                    render: (_, r) => (
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeBenefit(r.id)} />
                    ),
                  },
                ]}
              />
              <Button type="dashed" icon={<PlusOutlined />} onClick={addBenefit} style={{ marginTop: 8 }}>
                Add benefit
              </Button>
            </>
          )}
        </>
      )}
    </Spin>
  );

  return (
    <>
      {content}
      {showFooter && (
        <div style={{ marginTop: 16 }}>
          <Button type="primary" loading={saving} onClick={onSave}>
            Save
          </Button>
        </div>
      )}
    </>
  );
};
